import { noop } from 'lodash';
import { ReactNode, createContext, useContext } from 'react';

import {
  actionGetNotificationCount,
  actionGetNotifications,
  actionMarkNotificationReadById,
  actionMarkNotificationsRead,
} from '@libraries/api/notification/notification.function';
import { INotification, INotificationCount } from '@libraries/api/notification/notification.types';
import { useAuth } from './useAuth';

interface IUseNotification {
  notifications: INotification[];
  count: number;
  isLoading: boolean;
  refetchList: () => void;
  markRead: () => void;
  markReadById: (id: string) => void;
}

const NotificationContext = createContext<IUseNotification>({
  notifications: [],
  count: 0,
  isLoading: false,
  refetchList: noop,
  markRead: noop,
  markReadById: noop,
});

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuth } = useAuth();

  const { data: countData, refetch: refetchCount } = actionGetNotificationCount(isAuth);

  const {
    data: listData,
    isLoading,
    refetch: refetchList,
  } = actionGetNotifications({ page: 1, length: 20 }, false);

  const { mutate: markReadMutate } = actionMarkNotificationsRead();
  const { mutate: markReadByIdMutate } = actionMarkNotificationReadById();

  const markRead = () => {
    markReadMutate(undefined, {
      onSettled: () => {
        refetchCount();
        refetchList();
      },
    });
  };

  const markReadById = (id: string) => {
    markReadByIdMutate(id, {
      onSettled: () => {
        refetchCount();
        refetchList();
      },
    });
  };

  const value: IUseNotification = {
    notifications: listData?.data ?? [],
    count: countData?.count ?? 0,
    isLoading,
    refetchList,
    markRead,
    markReadById,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): IUseNotification => useContext(NotificationContext);
